import { Badge, Checkbox } from "@xola/ui-kit";
import React, { useMemo, useState } from "react";
import { Partner } from "types";
import { SelectionState, useSelection } from "../../hooks/useSelection";
import { FilterMenuButton } from "../Dashboard/Filters/FilterMenuButton";
import { FilterItem } from "../Dashboard/Filters/ListFilter";
import { MultiSelectPopover } from "../MultiSelectPopover";
import { stringIncludes } from "@/hooks/useSearch";
import { useVisibility } from "@/hooks/useVisibility";

export interface ReportSelectionProps {
    label: string;
    title: string;
    value: SelectionState;
    data: Partner[];
    onChange: (value: SelectionState) => void;
}

export const ReportSelection = ({ label, title, value, data, onChange }: ReportSelectionProps) => {
    const [isVisible, { hide, toggle }] = useVisibility(false);
    const [searchTerm, setSearchTerm] = useState("");
    const handleSearch = (text: string): void => setSearchTerm(text);
    const filteredData = searchTerm ? data.filter((partner) => stringIncludes(searchTerm, partner.name)) : data;

    const defaultSelection = useMemo(() => ({ all: value.all, selected: value.selected }), [value]);
    const isSubset = searchTerm.length > 0;
    const selection = useSelection(filteredData, isSubset, defaultSelection);

    const handleApply = () => {
        setSearchTerm("");
        onChange({ all: selection.state.all, selected: selection.state.selected });
        hide();
    };

    const handleCancel = () => {
        selection.setState(defaultSelection);
        setSearchTerm("");
        hide();
    };

    return (
        <MultiSelectPopover
            title={title}
            isVisible={isVisible}
            target={
                <div>
                    <FilterMenuButton
                        isDropdown
                        text={label}
                        className="h-10 items-center"
                        selectedCount={selection.count}
                        onClick={toggle}
                    />
                </div>
            }
            onApply={handleApply}
            onCancel={handleCancel}
            onSearch={handleSearch}
        >
            <div className="h-60">
                <MultiSelectPopover.Item key="selectAll" className="bg-gray-lighter">
                    <Checkbox
                        label="Select All"
                        checked={selection.isSelectedAll}
                        onChange={() => selection.selectAll()}
                    />
                    <Badge className="text-sm text-gray-darker" size="small">
                        {selection.count} Selected
                    </Badge>
                </MultiSelectPopover.Item>
                {filteredData.length > 0 ? (
                    filteredData.map((item) => (
                        <FilterItem
                            key={item.id}
                            label={item.name}
                            isChecked={selection.isSelected(item)}
                            className="h-12 border-b border-gray-light px-3.5 py-4 last:border-b-0"
                            onChange={() => selection.select(item)}
                        />
                    ))
                ) : (
                    <div className="flex justify-center">
                        <span className="pb-4 pt-4 text-lg font-semibold text-black">No results found</span>
                    </div>
                )}
            </div>
        </MultiSelectPopover>
    );
};
